import { getAuthHeaders, getJwt } from "./authHeaders";
import Cookies from "js-cookie";
import {
  buildFavouriteTeamToggle,
  type Favourites,
} from "./favouriteTeams";

const STORAGE_KEY = "c8_pending_favourite_team";
const MAX_TEAM_NAME_LENGTH = 80;

function sanitizeTeamName(raw: string | null | undefined): string {
  return String(raw || "")
    .replace(/,/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_TEAM_NAME_LENGTH);
}

/** Remember a team to favourite after the guest signs up or logs in. */
export function persistPendingFavouriteTeam(teamName: string): void {
  const name = sanitizeTeamName(teamName);
  if (!name) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, name);
  } catch {
    /* ignore */
  }
}

/** Capture `?favteam=` from login/signup (or any) search string. */
export function persistPendingFavouriteTeamFromSearch(search: string): void {
  try {
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    const team = params.get("favteam");
    if (team?.trim()) persistPendingFavouriteTeam(team);
  } catch {
    /* ignore */
  }
}

export function readPendingFavouriteTeam(): string | null {
  try {
    const name = sanitizeTeamName(sessionStorage.getItem(STORAGE_KEY));
    return name || null;
  } catch {
    return null;
  }
}

export function clearPendingFavouriteTeam(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Signup URL that carries the team through auth and returns the guest to `backPath`. */
export function signupPathForFavouriteTeam(teamName: string, backPath: string): string {
  const name = sanitizeTeamName(teamName);
  const params = new URLSearchParams();
  if (name) params.set("favteam", name);
  const back = String(backPath || "").trim();
  if (back) params.set("back", back);
  const query = params.toString();
  return query ? `/signup?${query}` : "/signup";
}

/**
 * After auth, add the pending team to favourites (always an add, never a remove).
 * Clears storage only on success so a transient failure can retry.
 */
export async function tryClaimPendingFavouriteTeamFromStorage(): Promise<Favourites | null> {
  const name = readPendingFavouriteTeam();
  if (!name) return null;
  if (!Cookies.get("c8_session_token") && !getJwt()) return null;

  const plan = buildFavouriteTeamToggle({}, name);
  if (!plan) {
    clearPendingFavouriteTeam();
    return null;
  }

  try {
    const res = await fetch(`${process.env.API_URL}/account/favourite-teams/toggle`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ ops: plan.ops }),
    });
    if (!res.ok) {
      if (res.status === 400 || res.status === 401 || res.status === 403) {
        clearPendingFavouriteTeam();
      }
      return null;
    }
    clearPendingFavouriteTeam();
    const data = (await res.json()) as { favourites?: Partial<Favourites> };
    if (data.favourites && typeof data.favourites === "object") {
      return { ...plan.next, ...data.favourites };
    }
    return plan.next;
  } catch {
    return null;
  }
}
